import React, { useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  SelectChangeEvent,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { translate as t } from 'hooks/useTranslations';
import { useRiskPoolOrgContext } from '../../RiskPoolOrgContext';
import { usePolicyContext } from '../PolicyContext';
import usePolicyTrends from '../_data_sources/usePolicyTrends';
import useDTBTrends from '../_data_sources/useDTBTrends';
import { neutral, warning, primary, success, error } from '_lex/_foundations/phxColors';
import OrganizationalTrendsFilters from './OrganizationalTrendsFilters';

type ViewMode = 'policy' | 'dtb';

interface OrganizationalTrendsProps {
  selectedManual: string;
  onManualChange: (event: SelectChangeEvent<string>) => void;
  onReportSelect: (reportType: string) => void;
}

export function OrganizationalTrends({ selectedManual, onManualChange, onReportSelect }: OrganizationalTrendsProps) {
  const { orgId, queryParams } = useRiskPoolOrgContext();
  const { manualId } = usePolicyContext();
  const [viewMode, setViewMode] = useState<ViewMode>('policy');

  const { data: policyData, isLoading: isPolicyLoading } = usePolicyTrends(orgId, {
    ...queryParams,
    manualId: manualId !== 'all' ? manualId : undefined,
  });
  const { data: dtbData, isLoading: isDTBLoading } = useDTBTrends(orgId, {
    ...queryParams,
    manualId: manualId !== 'all' ? manualId : undefined,
  });

  const isLoading = viewMode === 'policy' ? isPolicyLoading : isDTBLoading;
  const policyChartData = policyData?.metrics ?? [];
  const dtbChartData = dtbData?.metrics ?? [];
  const chartData = viewMode === 'policy' ? policyChartData : dtbChartData;

  const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newView: ViewMode | null) => {
    if (newView !== null) {
      setViewMode(newView);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <OrganizationalTrendsFilters
          selectedManual={selectedManual}
          onManualChange={onManualChange}
          onReportSelect={onReportSelect}
        />
        <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}`, mt: 3 }}>
          <CardHeader title={t('dashboard.riskPool.riskPoolOrg.trends.title')} />
          <CardContent>
            <Box sx={{ width: '100%', height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!chartData.length) {
    return null;
  }

  // Format data for chart
  const xAxisData = chartData.map((point) => {
    const date = new Date(point.period);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  });

  // Prepare series and legend based on view mode
  let series: any[] = [];
  let legendItems: Array<{ label: string; color: string }> = [];

  if (viewMode === 'policy' && policyChartData.length > 0) {
    series = [
      {
        id: 'pending',
        label: t('dashboard.riskPool.riskPoolOrg.trends.pendingUpdates'),
        data: policyChartData.map((d) => d.lexipolUpdates.pendingCount),
        stack: 'updates',
        color: warning[600],
      },
      {
        id: 'processed',
        label: t('dashboard.riskPool.riskPoolOrg.trends.processedUpdates'),
        data: policyChartData.map((d) => d.lexipolUpdates.processedCount),
        stack: 'updates',
        color: warning[200],
      },
      {
        id: 'overdue',
        label: t('dashboard.riskPool.riskPoolOrg.trends.overdueAcknowledgements'),
        data: policyChartData.map(
          (d) => d.assignmentsNotCompleted.overdueByLessThanWeek + d.assignmentsNotCompleted.overdueByWeekOrMore
        ),
        stack: 'acknowledgements',
        color: primary[500],
      },
      {
        id: 'completed',
        label: t('dashboard.riskPool.riskPoolOrg.trends.completedAcknowledgements'),
        data: policyChartData.map(
          (d) =>
            d.assignmentsCompleted.onTime +
            d.assignmentsCompleted.overdueByLessThanWeek +
            d.assignmentsCompleted.overdueByWeekOrMore
        ),
        stack: 'acknowledgements',
        color: primary[200],
      },
    ];

    legendItems = [
      { label: t('dashboard.riskPool.riskPoolOrg.trends.pendingUpdates'), color: warning[600] },
      { label: t('dashboard.riskPool.riskPoolOrg.trends.processedUpdates'), color: warning[200] },
      { label: t('dashboard.riskPool.riskPoolOrg.trends.overdueAcknowledgements'), color: primary[500] },
      { label: t('dashboard.riskPool.riskPoolOrg.trends.completedAcknowledgements'), color: primary[200] },
    ];
  } else if (viewMode === 'dtb' && dtbChartData.length > 0) {
    series = [
      {
        id: 'dtbCompletedOnTime',
        label: t('dashboard.riskPool.riskPoolOrg.trends.dtbCompletedOnTime'),
        data: dtbChartData.map((d) => d.completedDTBs.onTime),
        stack: 'dtbs',
        color: success[800],
      },
      {
        id: 'dtbCompletedLate',
        label: t('dashboard.riskPool.riskPoolOrg.trends.dtbCompletedLate'),
        data: dtbChartData.map((d) => d.completedDTBs.late),
        stack: 'dtbs',
        color: success[600],
      },
      {
        id: 'overdue',
        label: t('dashboard.riskPool.riskPoolOrg.trends.overdueAcknowledgements'),
        data: dtbChartData.map(
          (d) => d.assignmentsNotCompleted.overdueByLessThanWeek + d.assignmentsNotCompleted.overdueByWeekOrMore
        ),
        stack: 'acknowledgements',
        color: error[800],
      },
      {
        id: 'completed',
        label: t('dashboard.riskPool.riskPoolOrg.trends.completedAcknowledgements'),
        data: dtbChartData.map(
          (d) =>
            d.assignmentsCompleted.onTime +
            d.assignmentsCompleted.overdueByLessThanWeek +
            d.assignmentsCompleted.overdueByWeekOrMore
        ),
        stack: 'acknowledgements',
        color: error[500],
      },
    ];

    legendItems = [
      { label: t('dashboard.riskPool.riskPoolOrg.trends.dtbCompletedOnTime'), color: success[800] },
      { label: t('dashboard.riskPool.riskPoolOrg.trends.dtbCompletedLate'), color: success[600] },
      { label: t('dashboard.riskPool.riskPoolOrg.trends.overdueAcknowledgements'), color: error[800] },
      { label: t('dashboard.riskPool.riskPoolOrg.trends.completedAcknowledgements'), color: error[500] },
    ];
  }

  return (
    <Box>
      <OrganizationalTrendsFilters
        selectedManual={selectedManual}
        onManualChange={onManualChange}
        onReportSelect={onReportSelect}
      />
      <Card sx={{ borderRadius: 3, border: `1px solid ${neutral[300]}`, mt: 3 }}>
        <CardHeader
          title={t('dashboard.riskPool.riskPoolOrg.trends.title')}
          action={
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              sx={{
                borderRadius: 2,
                border: `1px solid ${neutral[200]}`,
                overflow: 'hidden',
                '& .MuiToggleButton-root': {
                  px: 2,
                  py: 1.5,
                  border: 'none',
                  borderRight: `1px solid ${neutral[200]}`,
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '21px',
                  color: neutral[800],
                  textTransform: 'none',
                  '&:last-of-type': {
                    borderRight: 'none',
                  },
                  '&.Mui-selected': {
                    backgroundColor: neutral[50],
                    color: neutral[800],
                  },
                  '&:not(.Mui-selected)': {
                    backgroundColor: neutral[25],
                  },
                },
              }}
            >
              <ToggleButton value='policy'>{t('dashboard.riskPool.riskPoolOrg.trends.policyTab')}</ToggleButton>
              <ToggleButton value='dtb'>{t('dashboard.riskPool.riskPoolOrg.trends.dtbTab')}</ToggleButton>
            </ToggleButtonGroup>
          }
        />
        <CardContent>
          {/* Chart */}
          <Box sx={{ width: '100%', height: 287, pt: 2.5, pb: 2.5, backgroundColor: 'white' }}>
            <BarChart
              xAxis={[
                {
                  scaleType: 'band',
                  data: xAxisData,
                },
              ]}
              series={series}
              height={287}
              margin={{ top: 10, bottom: 30, left: 60, right: 10 }}
              slotProps={{
                legend: { hidden: true },
              }}
            />
          </Box>

          {/* Legend */}
          <Box
            sx={{
              pt: 6,
              pb: 2,
            }}
          >
            <Stack
              direction='row'
              spacing={4}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              {legendItems.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: item.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      color: neutral[500],
                      fontSize: 12,
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      lineHeight: '16.8px',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default OrganizationalTrends;
